import { AssessmentStateEntry, GraderCosmeticComponent } from "@pandas/assessment-engine";
import type { IdentificationData, PaymentMethod, PriceData } from "@pandas/http-client";
import { StateCreator } from "zustand";
import type { ScreeningFallbackTrigger } from "@/components/image-capture/screening/screeningFallback";
import { PhoneCaptureAngles } from "@/components/phone-capture/capture-wizard/flows";
import getFlow from "@/utils/getFlow";
import { Storage } from "../index";

export type SelectedPromo = {
	code: string;
	validationKey?: string;
};

export interface Photo {
	url: string;
	name: string;
	components: GraderCosmeticComponent[];
	angle: PhoneCaptureAngles;
}

export type PhotoSet = Partial<Record<PhoneCaptureAngles, Photo>>;

export type ScreeningFallback = { trigger: ScreeningFallbackTrigger; shot: string; atLevel: number };

export interface SessionInfoSlice {
	sessionId: string | null;
	gradeRequestId: string | null;
	timer: {
		totalTimeout: number;
		timeRemaining: number;
		popupTimeout: number;
		reset: boolean;
	} | null;
	photos: PhotoSet | null;
	capturedAngles: PhoneCaptureAngles[];
	imeiInfo: IdentificationData | null;
	priceInfo: PriceData | null;
	maxPriceInfo: PriceData | null;
	selectedPromo: SelectedPromo | null;
	assessmentEngineState: Array<AssessmentStateEntry>;
	selectedPaymentMethod: PaymentMethod | null;
	finalizationInstructionsHtml: string | null;
	screeningFallback: ScreeningFallback | null;
	setScreeningFallback: (fallback: ScreeningFallback) => void;
	setImeiInfo: (imeiInfo: IdentificationData) => void;
	setPriceInfo: (priceInfo: PriceData) => void;
	setMaxPriceInfo: (priceInfo: PriceData) => void;
	setSelectedPromo: (promo: SelectedPromo) => void;
	setPhotos: (photos: PhotoSet) => void;
	addCapturedAngle: (angle: PhoneCaptureAngles) => void;
	clearCapturedAngles: () => void;
	reduceTimeRemaining: () => void;
	setTimer: (timeouts: { totalTimeout: number; popupTimeout: number }) => void;
	resetTimer: () => void;
	clearTimer: () => void;
	setSessionId: (sessionId: string) => void;
	setGradeRequestId: (gradeRequestId: string | null) => void;
	resetSessionInfo: () => void;
	setAttribute: (attribute: keyof SessionInfoSlice, value: boolean | string | string[] | null) => void;
	setAssessmentEngineState: (state: Array<AssessmentStateEntry>) => void;
	setSelectedPaymentMethod: (paymentMethod: PaymentMethod | null) => void;
	setFinalizationInstructionsHtml: (finalizationInstructionsHtml: string) => void;
}

const initialState = {
	sessionId: null,
	gradeRequestId: null,
	timer: null,
	photos: null,
	capturedAngles: [] as PhoneCaptureAngles[],
	imeiInfo: null,
	priceInfo: null,
	maxPriceInfo: null,
	selectedPromo: null,
	assessmentEngineState: [],
	selectedPaymentMethod: null,
	finalizationInstructionsHtml: null,
	screeningFallback: null,
};

export const createSessionSlice: StateCreator<Storage, [], [], SessionInfoSlice> = (set) => ({
	...initialState,
	setScreeningFallback: (fallback: ScreeningFallback) => set(() => ({ screeningFallback: fallback })),
	setImeiInfo: (imeiInfoPayload: IdentificationData) =>
		set((state) => ({
			imeiInfo: {
				...state.imeiInfo,
				...imeiInfoPayload,
			},
		})),
	setPriceInfo: (priceInfo: PriceData) =>
		set(() => ({
			priceInfo: priceInfo,
		})),
	setMaxPriceInfo: (priceInfo: PriceData) =>
		set(() => ({
			maxPriceInfo: priceInfo,
		})),
	setSelectedPromo: (promo: SelectedPromo) =>
		set(() => ({
			selectedPromo: promo,
		})),
	setTimer: (timeouts: { totalTimeout: number; popupTimeout: number }) =>
		set(() => {
			const { isFromHomeFlow } = getFlow();
			if (isFromHomeFlow) return { timer: null };
			return {
				timer: {
					totalTimeout: timeouts.totalTimeout,
					timeRemaining: timeouts.totalTimeout,
					popupTimeout: timeouts.popupTimeout,
					reset: false,
				},
			};
		}),
	reduceTimeRemaining: () =>
		set((state) => ({
			timer: state.timer ? { ...state.timer, timeRemaining: state.timer.timeRemaining - 1 } : null,
		})),
	resetTimer: () =>
		set((state) => ({
			timer: state.timer ? { ...state.timer, timeRemaining: state.timer.totalTimeout } : null,
		})),
	clearTimer: () => set(() => ({ timer: null })),
	setSessionId: (sessionId: string) =>
		set(() => ({
			sessionId: sessionId,
		})),
	setGradeRequestId: (gradeRequestId: string | null) =>
		set(() => ({
			gradeRequestId: gradeRequestId,
		})),
	resetSessionInfo: () =>
		set((state) => {
			state.resetUserInfo();
			state.resetPeerSession();
			state.clearProgressBar();
			return {
				...initialState,
			};
		}),
	setAttribute: (attribute: string, value: boolean | string | string[] | null) =>
		set(() => ({
			[attribute]: value,
		})),
	setPhotos: (newPhotos: PhotoSet) => {
		set((state) => ({
			photos: state.photos ? { ...state.photos, ...newPhotos } : newPhotos,
		}));
	},
	addCapturedAngle: (angle: PhoneCaptureAngles) => {
		set((state) =>
			state.capturedAngles.includes(angle) ? state : { capturedAngles: [...state.capturedAngles, angle] },
		);
	},
	clearCapturedAngles: () => set(() => ({ capturedAngles: [] })),
	setAssessmentEngineState: (state: Array<AssessmentStateEntry>) =>
		set(() => ({
			assessmentEngineState: state,
		})),
	setSelectedPaymentMethod: (paymentMethod: PaymentMethod | null) =>
		set(() => ({
			selectedPaymentMethod: paymentMethod,
		})),
	setFinalizationInstructionsHtml: (finalizationInstructionsHtml: string) =>
		set(() => ({
			finalizationInstructionsHtml: finalizationInstructionsHtml,
		})),
});
